import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { User } from '@supabase/supabase-js'
import { supabase, supabaseConfigurado } from '../lib/supabase'

type AuthContextValue = {
  user: User | null
  isAuthenticated: boolean
  bootstrapping: boolean
  loading: boolean
  error: string | null
  login: (email: string, senha: string) => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
  /**
   * Permissões do usuário logado, para mostrar ou esconder telas e botões.
   * É só conforto de interface: quem barra de verdade é o RLS no banco.
   */
  pode: (permissao: string) => boolean
  permissoesCarregando: boolean
  /** Senha provisória (criada ou redefinida por outra pessoa): o app pede uma nova. */
  precisaTrocarSenha: boolean
  /** Chamado depois de trocar a senha: sai da senha provisória. */
  senhaTrocada: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function mensagemDeErro(raw: string): string {
  if (/invalid login credentials/i.test(raw)) return 'Email ou senha incorretos'
  if (/email not confirmed/i.test(raw)) return 'Email ainda não confirmado'
  return raw
}

/** Sessão do Supabase Auth. Usuários são criados no painel do Supabase — não há cadastro aberto. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [bootstrapping, setBootstrapping] = useState(supabaseConfigurado)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!supabaseConfigurado) return
    let cancelled = false
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setUser(data.session?.user ?? null)
      setBootstrapping(false)
    })
    const { data: inscricao } = supabase.auth.onAuthStateChange((_evento, sessao) => {
      setUser(sessao?.user ?? null)
    })
    return () => {
      cancelled = true
      inscricao.subscription.unsubscribe()
    }
  }, [])

  const login = useCallback(async (email: string, senha: string) => {
    if (!supabaseConfigurado) {
      const msg = 'Supabase não configurado — preencha o web/.env.local'
      setError(msg)
      throw new Error(msg)
    }
    setLoading(true)
    setError(null)
    try {
      const { data, error: erro } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: senha,
      })
      if (erro) throw erro
      setUser(data.user)
    } catch (e) {
      setUser(null)
      setError(mensagemDeErro(e instanceof Error ? e.message : 'Falha no login'))
      throw e
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    setLoading(true)
    try {
      await supabase.auth.signOut()
    } finally {
      setUser(null)
      setLoading(false)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])

  const [permissoes, setPermissoes] = useState<Set<string>>(new Set())
  const [precisaTrocarSenha, setPrecisaTrocarSenha] = useState(false)
  // De quem são as permissões carregadas. Derivar o "carregando" disto (e não de um flag
  // ligado no efeito) fecha o render em que o usuário já existe e o efeito ainda não rodou —
  // nele o RequirePermissao via "sem permissão" e mandava quem recarregou /leads para o início.
  const [permissoesDe, setPermissoesDe] = useState<string | null>(null)
  const userId = user?.id
  const permissoesCarregando = Boolean(userId) && permissoesDe !== userId

  useEffect(() => {
    if (!userId) {
      setPermissoes(new Set())
      setPermissoesDe(null)
      return
    }
    let cancelled = false
    supabase
      .from('usuarios')
      .select('trocar_senha')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setPrecisaTrocarSenha(Boolean(data?.trocar_senha))
      })
    supabase.rpc('minhas_permissoes').then(({ data, error: erro }) => {
      if (cancelled) return
      // Falhou ao carregar: sem permissão nenhuma na tela (o banco barra de qualquer jeito).
      setPermissoes(new Set(erro ? [] : ((data ?? []) as string[])))
      setPermissoesDe(userId)
    })
    return () => {
      cancelled = true
    }
  }, [userId])

  const pode = useCallback((permissao: string) => permissoes.has(permissao), [permissoes])

  const senhaTrocada = useCallback(async () => {
    await supabase.rpc('senha_trocada')
    setPrecisaTrocarSenha(false)
  }, [])

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      bootstrapping,
      loading,
      error,
      login,
      logout,
      clearError,
      pode,
      permissoesCarregando,
      precisaTrocarSenha,
      senhaTrocada,
    }),
    [user, bootstrapping, loading, error, login, logout, clearError, pode, permissoesCarregando, precisaTrocarSenha, senhaTrocada],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth fora de AuthProvider')
  return ctx
}
